// 복사 모달 - 스킬/효과/트리거를 다른 보스나 공통으로 복사
import React, { useEffect, useState } from 'react';
import { Modal, Select, Alert, Typography, Space, Spin } from 'antd';
import { BossInfo, BossSkill, BossEffect } from '../../types';
import { bossApi, utilityApi } from '../../services/api';
import { useApp } from '../../contexts/AppContext';
import { message } from 'antd';

const { Option } = Select;
const { Text } = Typography;

interface CopyModalProps {
  visible: boolean;
  onCancel: () => void;
  onSuccess: () => void;
}

const CopyModal: React.FC<CopyModalProps> = ({ visible, onCancel, onSuccess }) => {
  const { state } = useApp();
  const { copyModalState } = state;
  
  const [bossList, setBossList] = useState<BossInfo[]>([]);
  const [targetBoss, setTargetBoss] = useState<string>('');
  const [loading, setLoading] = useState(false);
  const [loadingBosses, setLoadingBosses] = useState(false);

  // 보스 목록 로드
  useEffect(() => {
    if (visible) {
      loadBossList();
    }
  }, [visible]);

  const loadBossList = async () => {
    try {
      setLoadingBosses(true);
      const bosses = await bossApi.getAll();
      setBossList(bosses);
    } catch (error) {
      console.error('보스 목록 로드 실패:', error);
      message.error('보스 목록을 불러오는데 실패했습니다.');
    } finally {
      setLoadingBosses(false);
    }
  };

  const handleCopy = async () => {
    if (!targetBoss) {
      message.warning('대상을 선택하세요.');
      return;
    }

    if (!copyModalState.sourceItem) {
      message.error('복사할 항목이 없습니다.');
      return;
    }

    try {
      setLoading(true);
      
      const result = await utilityApi.copyResource({
        sourceKey: getSourceKey(),
        targetBossCode: targetBoss,
        resourceType: copyModalState.itemType,
        copyRelations: true, // 관계도 함께 복사
      });

      message.success(`${copyModalState.itemName}을(를) 성공적으로 복사했습니다.`);
      onSuccess();
      resetModal();
    } catch (error) {
      console.error('복사 실패:', error);
      message.error('복사에 실패했습니다.');
    } finally {
      setLoading(false);
    }
  };

  const getSourceKey = () => {
    const { itemType, sourceItem } = copyModalState;
    
    switch (itemType) {
      case 'skill':
        return (sourceItem as BossSkill).key;
      case 'effect':
        return (sourceItem as BossEffect).key;
      case 'trigger':
        return (sourceItem as any).id;
      default:
        return '';
    }
  };

  const resetModal = () => {
    setTargetBoss('');
    onCancel();
  };

  // 현재 항목의 소속 보스 코드
  const currentBossCode = copyModalState.sourceItem?.boss_code;

  return (
    <Modal
      title={`${copyModalState.itemName} 복사`}
      open={visible}
      onCancel={resetModal}
      onOk={handleCopy}
      confirmLoading={loading}
      okText="복사"
      cancelText="취소"
    >
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Alert
          message="복사 정보"
          description={
            <Space direction="vertical">
              <Text>원본: <strong>{copyModalState.itemName}</strong></Text>
              <Text>타입: <strong>{
                copyModalState.itemType === 'skill' ? '스킬' :
                copyModalState.itemType === 'effect' ? '효과' : '트리거'
              }</strong></Text>
              <Text>현재 소속: <strong>{
                currentBossCode === 'COMMON' ? '공통 자원' :
                currentBossCode ? bossList.find(b => b.boss_code === currentBossCode)?.boss_name || currentBossCode :
                '알 수 없음'
              }</strong></Text>
            </Space>
          }
          type="info"
        />

        <div>
          <Text strong>복사 대상 선택:</Text>
          <Select
            style={{ width: '100%', marginTop: 8 }}
            placeholder="복사할 대상을 선택하세요"
            value={targetBoss}
            onChange={setTargetBoss}
            loading={loadingBosses}
            disabled={loading}
            showSearch
            filterOption={(input, option) => {
              const label = option?.label || option?.value || '';
              return String(label).toLowerCase().includes(input.toLowerCase());
            }}
          >
            <Option value="COMMON" disabled={currentBossCode === 'COMMON'}>
              공통 자원으로 복사
            </Option>
            
            {loadingBosses ? (
              <Option value="" disabled>
                <Spin size="small" /> 보스 목록 로드 중...
              </Option>
            ) : (
              <>
                {bossList.map(boss => (
                  <Option 
                    key={boss.boss_code} 
                    value={boss.boss_code}
                    disabled={boss.boss_code === currentBossCode}
                  >
                    {boss.boss_name} ({boss.boss_code})
                  </Option>
                ))}
              </>
            )}
          </Select>
        </div>

        {targetBoss && (
          <Alert
            message="복사 안내"
            description={
              <Space direction="vertical" size="small">
                <Text>• 항목이 복사되면 새로운 Key가 자동으로 생성됩니다.</Text>
                <Text>• 이름 뒤에 "(복사본)" 이 추가됩니다.</Text>
                {copyModalState.itemType === 'skill' && (
                  <Text>• 스킬에 연결된 효과 관계도 함께 복사됩니다.</Text>
                )}
                {copyModalState.itemType === 'trigger' && (
                  <Text>• 트리거에 연결된 효과/스킬/대사 관계도 함께 복사됩니다.</Text>
                )}
              </Space>
            }
            type="warning"
            showIcon
          />
        )}
      </Space>
    </Modal>
  );
};

export default CopyModal;